import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StatusBarTooltip from './StatusBarTooltip';

export interface StatusBarItem {
    label: string;
    value: number;
    color: string;
    className: string;
    statusEnum?: number;
}

export interface StatusCardDateItem {
    label: string;
    value: string;
    isTBD?: boolean;
}

interface StatusCardProps {
    title: string;
    subtitle?: string;
    statusData: StatusBarItem[];
    onStatusClick?: (statusEnum: number, statusLabel: string) => void;
    onInfoClick?: () => void;
    infoIconTitle?: string;
    showInfoIcon?: boolean;
    actionButtons?: React.ReactNode;
    showLegendInFooter?: boolean;
    dateItems?: StatusCardDateItem[];
    children?: React.ReactNode;
}

const StatusCard: React.FC<StatusCardProps> = ({
    title,
    subtitle,
    statusData,
    onStatusClick,
    onInfoClick,
    infoIconTitle,
    showInfoIcon = false,
    actionButtons,
    showLegendInFooter = false,
    dateItems,
    children
}) => {
    // Calculate total for percentages
    const total = statusData.reduce((sum, item) => sum + item.value, 0);

    const renderStatusBar = () => (
        <div className="status-bar-container">
            <div className="status-bar">
                {statusData.map((item, index) => {
                    const MIN_VISUAL_PERCENTAGE = 5;
                    const percentage = (item.value / total) * 100;

                    // Calculate minimum flexGrow to achieve 5% visual width
                    // flexGrow works proportionally, so 5% of total ensures 5% width
                    const minFlexGrow = total * (MIN_VISUAL_PERCENTAGE / 100);

                    // Use the larger of actual value or minimum flexGrow
                    const flexGrow = Math.max(item.value, minFlexGrow);

                    return (
                        <StatusBarTooltip
                            key={index}
                            label={item.label}
                            value={item.value}
                            percentage={percentage}
                            flexGrow={flexGrow}
                        >
                            <div
                                className={`status-bar-segment ${
                                    item.className
                                } ${
                                    onStatusClick &&
                                    item.statusEnum !== undefined
                                        ? 'clickable'
                                        : ''
                                }`}
                                style={{
                                    backgroundColor: item.color
                                }}
                                onClick={() => {
                                    if (
                                        onStatusClick &&
                                        item.statusEnum !== undefined
                                    ) {
                                        onStatusClick(
                                            item.statusEnum,
                                            item.label
                                        );
                                    }
                                }}
                            >
                                {percentage >= 15 && (
                                    <span className="segment-label">
                                        {item.label} {Math.floor(percentage)}%
                                    </span>
                                )}
                            </div>
                        </StatusBarTooltip>
                    );
                })}
            </div>
        </div>
    );

    const renderLegend = () => (
        <div className="status-legend">
            {statusData.map((item, index) => (
                <div key={index} className="legend-item">
                    <span
                        className="legend-dot"
                        style={{ backgroundColor: item.color }}
                    />
                    <span className="legend-label">
                        {item.label}: {item.value}
                    </span>
                </div>
            ))}
        </div>
    );

    return (
        <div className="status-card">
            <div className="status-card-header">
                <div className="status-card-title">
                    <h3>{title}</h3>
                    {showInfoIcon && onInfoClick && (
                        <span
                            className="info-icon-wrapper"
                            onClick={onInfoClick}
                            title={infoIconTitle}
                        >
                            <FontAwesomeIcon
                                icon={['far', 'info-circle']}
                                className="info-icon clickable"
                            />
                        </span>
                    )}
                    {subtitle && (
                        <p className="status-card-subtitle">{subtitle}</p>
                    )}
                </div>
            </div>

            {dateItems && dateItems.length > 0 && (
                <div className="status-card-dates">
                    {dateItems.map((item, index) => (
                        <div key={index} className="status-card-date-item">
                            <span className="date-label">{item.label}:</span>
                            <span
                                className={`date-value ${
                                    item.isTBD ? 'tbd' : ''
                                }`}
                            >
                                {item.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {renderStatusBar()}

            {!showLegendInFooter && renderLegend()}

            {children}

            {(showLegendInFooter || actionButtons) && (
                <div className="status-card-actions">
                    {showLegendInFooter && renderLegend()}
                    {actionButtons && (
                        <div className="status-card-footer">
                            {actionButtons}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatusCard;
